import React, { useState, useEffect, useMemo } from 'react';
import { useAuthStore } from '@store/authStore';
import { apiClient } from '@/lib/api';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { Card, CardContent } from '@components/ui/Card';
import { EmptyState } from '@assets/images';
import { Skeleton } from '@components/ui/Skeleton';
import { QuoteCard, QuoteFilters, QuoteDetailsDrawer, QuoteSearchBar } from '../components';
import AdvanceInvoiceDrawer from '../components/AdvanceInvoiceDrawer';
import { RiArrowDownLine, RiLoader4Line } from '@remixicon/react';
import {
  searchQuotesByDate,
  searchQuotesByAirport,
  searchQuotesByClient,
  searchQuotesByQuoteNumber,
} from '../services/quoteSearchService';

const { SEARCH_TYPES } = QuoteSearchBar;

const PAGE_SIZE = 10;

// Include chain — every field QuoteCard reads. Same as the search service
// uses so cache keys line up when a filter narrows or widens.
// `files` + `passengers` are requested for the FE-side "TRIP_SHEET_IN_PROGRESS"
// promotion (see getEffectiveQuoteStatus) — a PASSENGERS_PENDING quote whose
// client has already submitted needs either `quote.files.request_client_passengers === '2'`
// or a non-empty `quote.passengers` to render as the promoted label. If BE
// ignores an unknown include the payload just doesn't grow — no harm.
const QUOTES_INCLUDE =
  'client,creator,sender,payments,items,files,passengers,bid_items,bid_items.bid_options,' +
  'bid_items.bid_options.aircraft,bid_items.bid_options.aircraft.custom_profile,' +
  'bid_items.bid_options.operator';

/**
 * Server row → QuoteCard shape. The BE emits `row_type: quote | invoice`
 * on a unified /financial/reports payload; QuoteCard branches on `_type`
 * for its render + drawer routing, so we translate here rather than
 * changing every downstream reader.
 */
const toQuoteCardRow = (row) => {
  const type = row.row_type === 'invoice' ? 'invoice' : 'quote';
  return {
    ...row,
    _type: type,
    id: row.row_id || row.quote_id || row.invoice_id,
    // QuoteCard reads `quote_number`; the endpoint sets it for quote rows
    // and leaves it null for invoice rows (invoice_number instead).
    quote_number: row.quote_number || row.invoice_number || row.reference_no || '',
    // QuoteCard reads `total_due`; endpoint unifies as `total_amount`.
    total_due: row.total_amount ?? row.total_due ?? 0,
    // Default status label consistent with the old dedup path.
    status: row.status || 'draft',
  };
};

/**
 * QuotesList Page
 * Main page displaying all quotes with filtering functionality
 */
const QuotesList = () => {

  // State management
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [selectedQuoteStatus, setSelectedQuoteStatus] = useState(null);
  const [selectedQuotePaymentStatus, setSelectedQuotePaymentStatus] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedInvoiceQuote, setSelectedInvoiceQuote] = useState(null);
  const [selectedInvoiceStatus, setSelectedInvoiceStatus] = useState(null);
  const [selectedInvoicePaymentStatus, setSelectedInvoicePaymentStatus] = useState(null);
  const [isInvoiceDrawerOpen, setIsInvoiceDrawerOpen] = useState(false);
  const { userProfile, isLoading: isAuthLoading, isAuthenticated, fetchUserProfile } = useAuthStore();

  // Search-by state (mirrors the mobile QuotesScreen behaviour).
  // When `searchType` is not 'none' the infinite query swaps to the matching
  // searchQuotesBy* helper, keyed by the chosen `searchValue`.
  const [searchType, setSearchType] = useState(SEARCH_TYPES.NONE);
  const [searchValue, setSearchValue] = useState(null);
  const [searchLabel, setSearchLabel] = useState('');

  // Catchup now runs on /track (where the on-screen flight rows are the
  // source of truth). The /quotes list doesn't carry flight data, so there's
  // nothing to feed the hook here. When the broker visits /track, the
  // catchup will update flight statuses + cascade to quote statuses —
  // those changes are picked up by /quotes via React Query invalidation.

  // Ensure user profile is loaded when authenticated but profile is missing
  useEffect(() => {
    if (isAuthenticated && !userProfile && !isAuthLoading) {
      fetchUserProfile();
    }
  }, [isAuthenticated, userProfile, isAuthLoading, fetchUserProfile]);

  // Get profile ID (API may return as 'id' or 'userprofile_id')
  const profileId = userProfile?.userprofile_id;

  // Role-based scope (self / org / platform) is derived server-side from
  // the auth token, so we no longer pass broker_org_id or created_by.
  // Broker/junior_broker → own rows; admin/finance → org; super_admin → all.

  // Status tab → server `status_search` param (BE renamed from `status`
  // when the unified /financial/reports endpoint learned to OR quote +
  // invoice statuses in one filter). 'all' omits the filter so the BE
  // returns everything in scope. Uppercased because the endpoint's
  // status enum is uppercase (DRAFT, SENT, ACCEPTED, …).
  const statusParam = activeFilter && activeFilter !== 'all'
    ? String(activeFilter).toUpperCase()
    : undefined;

  /**
   * Single infinite query against the unified /financial/reports endpoint.
   * Search modes route through service functions with the corresponding BE
   * param (reference_no_search / client_id / airport_search /
   * next_flight_date_from_to); no search → direct hit with just the status
   * filter. Every branch returns { data, hasMore } so getNextPageParam is
   * uniform.
   */
  const {
    data: quotesPages,
    isLoading: isQuotesLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error: quotesError,
  } = useInfiniteQuery({
    // Include activeFilter so switching tabs resets pagination to page 0.
    queryKey: ['quotes', 'list', activeFilter, searchType, searchValue],
    queryFn: async ({ pageParam = 0 }) => {
      const extras = statusParam ? { status_search: statusParam } : {};
      if (searchType === SEARCH_TYPES.DATE && searchValue) {
        return await searchQuotesByDate(searchValue, extras, pageParam, PAGE_SIZE);
      }
      if (searchType === SEARCH_TYPES.AIRPORT && searchValue) {
        return await searchQuotesByAirport(searchValue, extras, pageParam, PAGE_SIZE);
      }
      if (searchType === SEARCH_TYPES.CLIENT && searchValue) {
        return await searchQuotesByClient(searchValue, extras, pageParam, PAGE_SIZE);
      }
      if (searchType === SEARCH_TYPES.QUOTE_NUMBER && searchValue) {
        return await searchQuotesByQuoteNumber(searchValue, extras, pageParam, PAGE_SIZE);
      }
      const { items, has_more } = await apiClient.getFinancialReports({
        include: QUOTES_INCLUDE,
        sort_by: 'created_at',
        sort_order: 'desc',
        ...extras,
        skip: pageParam,
        limit: PAGE_SIZE,
      });
      const arr = Array.isArray(items) ? items : [];
      return { data: arr, hasMore: typeof has_more === 'boolean' ? has_more : arr.length === PAGE_SIZE };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.hasMore) return undefined;
      return allPages.length * PAGE_SIZE;
    },
    enabled: !!profileId,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false,
  });

  // Flatten paged rows, then translate to the QuoteCard shape. Server owns
  // ordering + dedup + status filtering, so no client-side merge or filter.
  const quotesData = useMemo(() => {
    const flat = (quotesPages?.pages || []).flatMap((p) => (p?.data || []));
    return flat.map(toQuoteCardRow);
  }, [quotesPages]);

  // Filter badge counts — /quotes/summary hits a dedicated aggregation
  // endpoint that returns per-status + per-payment-status counts across
  // the CURRENT scope (role-scoped server-side, narrowed by whatever
  // date/client filters the search bar has active). We deliberately do
  // NOT pass `status` here — the whole point of the badges is to show
  // how many rows sit in EACH status within the same backdrop, so
  // filtering by status would collapse every other badge to zero. Text
  // searches (airport, quote number) aren't representable in this
  // endpoint's contract, so those searches just fall back to unfiltered
  // org-wide counts — acceptable since the badges are a "how many exist
  // in each bucket" indicator, not a mirror of the current list view.
  const summaryFilters = useMemo(() => {
    if (searchType === SEARCH_TYPES.DATE && searchValue) {
      return { departure_date_from: searchValue, departure_date_to: searchValue };
    }
    if (searchType === SEARCH_TYPES.CLIENT && searchValue) {
      return { client_id: searchValue };
    }
    return {};
  }, [searchType, searchValue]);
  const { data: quotesSummary } = useQuery({
    queryKey: ['quotes', 'summary', summaryFilters],
    queryFn: () => apiClient.getQuotesSummary(summaryFilters),
    enabled: !!profileId,
    staleTime: 60 * 1000,
  });
  // Map BE's UPPERCASE status keys to the lowercase keys QuoteFilters
  // expects (e.g. PASSENGERS_PENDING → passengers_pending). Any status
  // returned by the summary that isn't listed as a filter tab just
  // falls through — the filter component reads keys it knows about and
  // ignores the rest.
  const statusCounts = useMemo(() => {
    const raw = quotesSummary?.status_summary || {};
    const out = {};
    Object.entries(raw).forEach(([k, v]) => {
      out[String(k).toLowerCase()] = Number(v) || 0;
    });
    out.all = Number(quotesSummary?.total) || 0;
    return out;
  }, [quotesSummary]);
  const totalCount = Number(quotesSummary?.total) || 0;

  /**
   * Handle filter change
   */
  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
  };

  /**
   * Handle a "Search By" selection from the QuoteSearchBar.
   * Setting `searchType`/`searchValue` re-keys the infinite query, which
   * automatically resets pagination and fetches page 0 in the chosen mode.
   */
  const handleSearchSelect = (type, value, label) => {
    if (!type || !value) return;
    setSearchType(type);
    setSearchValue(value);
    setSearchLabel(label || '');
  };

  const handleSearchClear = () => {
    setSearchType(SEARCH_TYPES.NONE);
    setSearchValue(null);
    setSearchLabel('');
  };

  /**
   * Handle viewing a quote
   */
  const handleViewQuote = (quote, status, paymentStatus) => {
    setSelectedQuoteId(quote.id);
    setSelectedQuoteStatus(status);
    setSelectedQuotePaymentStatus(paymentStatus ?? null);
    setIsDrawerOpen(true);
  };

  const handleViewInvoice = (quote, status, paymentStatus) => {
    setSelectedInvoiceQuote(quote);
    setSelectedInvoiceStatus(status);
    setSelectedInvoicePaymentStatus(paymentStatus ?? null);
    setIsInvoiceDrawerOpen(true);
  };

  console.log("quotesData", quotesData);

  return (
    <div className="pb-20 bg-white/50 backdrop-blur-sm rounded-[2rem] p-4 md:p-6">

      {/* Main Content */}
      <div className="w-full">
        <Card className="overflow-hidden !border-gray-100 !bg-transparent !shadow-none !border-0">
          <CardContent className="!p-1">
            {/* Filter + Search By — side by side on md+, stacked on narrow screens */}
            <div className="mb-6 flex flex-col items-center gap-3 w-full max-w-5xl mx-auto">
              {/* Desktop: filter takes the remaining row width on the left,
                  search bar sits at content width on the right.
                  Mobile (flex-col): filter on top, search below. */}
              <div className="flex flex-col items-center md:flex-row md:items-center gap-3 md:gap-6 w-full">
                {/* `md:flex-1` lets the filter pill grow to fill the row beside
                    the search bar — matches the original wide-pill look. */}
                <div className="w-full md:flex-1 md:min-w-0">
                  {/* Badge counts come from GET /quotes/summary — a
                      dedicated aggregation endpoint that returns
                      per-status counts across the current backdrop
                      (role-scoped server-side, narrowed by date/client
                      when those searches are active). See summaryFilters
                      above for what we pass through. */}
                  <QuoteFilters
                    activeFilter={activeFilter}
                    onFilterChange={handleFilterChange}
                    totalCount={totalCount}
                    statusCounts={statusCounts}
                  />
                </div>
                <div className="shrink-0">
                  <QuoteSearchBar
                    searchType={searchType}
                    searchValue={searchValue}
                    searchLabel={searchLabel}
                    brokerId={profileId}
                    onSelect={handleSearchSelect}
                    onClear={handleSearchClear}
                  />
                </div>
              </div>
              {quotesError && (
                <p className="text-xs text-red-600">
                  {quotesError?.message || 'Failed to load quotes.'}
                </p>
              )}
            </div>

            {isQuotesLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Card key={i} className="overflow-hidden">
                    <CardContent className="p-5 space-y-4">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-11 w-11 rounded-full" />
                        <div className="flex-1 space-y-2">
                          <Skeleton className="h-4 w-32" />
                          <Skeleton className="h-3 w-24" />
                        </div>
                      </div>
                      <Skeleton className="h-12 w-full" />
                      <div className="flex gap-2">
                        <Skeleton className="h-5 w-20" />
                      </div>
                      <Skeleton className="h-10 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : quotesData?.length === 0 ? (
              <Card className="max-w-md mx-auto !bg-transparent !border-0 !shadow-none">
                <CardContent className="p-12 text-center space-y-4">
                  <img src={EmptyState} alt="No quotes" className="w-30 h-30 mx-auto" />
                  <div>
                    <h3 className="text-md font-semibold text-gray-900 mb-2">
                      {searchType !== SEARCH_TYPES.NONE ? 'No quotes found' : 'No quotes created yet'}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {searchType !== SEARCH_TYPES.NONE
                        ? 'Try a different search criteria.'
                        : activeFilter === 'all'
                          ? <>Add aircraft to your shortlist to compare <br /> options and send a quote to your client.</>
                          : `No ${activeFilter} quotes at the moment.`}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4">
                  {quotesData?.map((quote) => (
                    <QuoteCard key={quote.id} quote={quote} onViewQuote={handleViewQuote} onViewInvoice={handleViewInvoice} />
                  ))}
                </div>
                {hasNextPage && (
                  <div className="flex justify-center mt-8">
                    <button
                      type="button"
                      onClick={() => fetchNextPage()}
                      disabled={isFetchingNextPage}
                      className={`group relative inline-flex items-center gap-2 px-6 py-3 rounded-full text-sm shadow-sm transition-all duration-200 ${
                        isFetchingNextPage
                          ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                          : 'bg-primary-600 text-white hover:bg-primary-700 hover:shadow-lg hover:-translate-y-0.5 active:translate-y-0 active:shadow-sm'
                      }`}
                    >
                      {isFetchingNextPage ? (
                        <>
                          <RiLoader4Line className="w-4 h-4 animate-spin" />
                          <span>Loading more…</span>
                        </>
                      ) : (
                        <>
                          <span>Load More</span>
                          <RiArrowDownLine className="w-4 h-4 transition-transform group-hover:translate-y-0.5" />
                        </>
                      )}
                    </button>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Quote Details Drawer */}
      <QuoteDetailsDrawer
        isOpen={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        quoteId={selectedQuoteId}
        status={selectedQuoteStatus}
        paymentStatus={selectedQuotePaymentStatus}
      />

      {/* Advance Invoice Drawer */}
      <AdvanceInvoiceDrawer
        isOpen={isInvoiceDrawerOpen}
        onClose={() => setIsInvoiceDrawerOpen(false)}
        invoice={selectedInvoiceQuote}
        status={selectedInvoiceStatus}
        paymentStatus={selectedInvoicePaymentStatus}
      />
    </div>
  );
};

export default QuotesList;